/**
 * Agent run-history summaries — pure reads over the governance ledger.
 *
 * WHY: the Agents showcase ("these are real governed workers") needs proof,
 * and the proof already exists: every agent run appends an `agent_run` entry
 * to the append-only activity ledger (src/lib/db/agents.ts → logActivity).
 * This module turns those raw ledger rows into per-agent summaries — recent
 * runs, ledgered run count, last-run timestamp, and a live "active now"
 * signal — plus a fleet-level pulse for the compact Home widget.
 *
 * Deliberately PURE (no db, no Date.now inside): callers inject `now`, so
 * every branch is unit-testable and the same math renders identically on the
 * Agents grid, the Home widget, and anywhere else that wants receipts.
 */

import type { ActivityEntry } from '../db/types';

/** One ledgered run, newest-first in `AgentRunSummary.recent`. */
export interface AgentRunRecord {
  /** ISO timestamp of the run (ledger `at`). */
  at: string;
  /** The run narration with the "<Agent name>: " prefix stripped. */
  text: string;
}

/** Everything the ledger proves about one agent's run history. */
export interface AgentRunSummary {
  agentId: string;
  /** Total `agent_run` ledger entries for this agent (all time). */
  runCount: number;
  /** ISO timestamp of the newest ledgered run, or null when never run. */
  lastRunAt: string | null;
  /** Newest-first ledgered runs, capped at `recentLimit` (default 3). */
  recent: AgentRunRecord[];
  /** True when the newest run landed within the active window. */
  activeNow: boolean;
}

/** Fleet-level rollup for the compact "Your agents" Home widget. */
export interface FleetPulse {
  /** Total deployed agents. */
  deployed: number;
  /** Agents whose lifecycle state is 'running'. */
  running: number;
  /** Agents with a ledgered run inside the active window ("active now"). */
  activeNow: number;
  /** Ledgered agent runs in the trailing 24 hours. */
  runs24h: number;
  /** ISO timestamp of the newest ledgered run across the fleet, or null. */
  lastRunAt: string | null;
}

/** A run within this window counts as "active now" (5 minutes). */
export const ACTIVE_RUN_WINDOW_MS = 5 * 60_000;

/** The `runs24h` window for the fleet pulse. */
export const FLEET_RUNS_WINDOW_MS = 24 * 60 * 60_000;

/** Default number of recent runs kept per agent summary. */
export const DEFAULT_RECENT_LIMIT = 3;

/** Options shared by the summary functions. All optional, all injectable. */
export interface RunHistoryOptions {
  /** "Current" epoch ms for the activeNow / runs24h windows. Default: Date.now(). */
  now?: number;
  /** Max recent runs kept per agent. Default: 3. */
  recentLimit?: number;
  /** Active-now window in ms. Default: ACTIVE_RUN_WINDOW_MS. */
  activeWindowMs?: number;
}

/** The minimal ledger-row shape this module reads (subset of ActivityEntry). */
export type LedgerRunRow = Pick<ActivityEntry, 'event' | 'at' | 'summary'> &
  Partial<Pick<ActivityEntry, 'subjectId' | 'kind' | 'source'>>;

/**
 * Strip the "<Agent name>: " prefix agents.ts stamps onto ledger summaries
 * so cards can show just the narration. Total: rows without the prefix
 * (or with a malformed one) come back unchanged.
 */
export function runTextFromSummary(summary: string): string {
  const i = summary.indexOf(': ');
  return i > 0 ? summary.slice(i + 2) : summary;
}

/** Keep only `agent_run` rows that identify their agent. */
function agentRunRows(entries: readonly LedgerRunRow[]): LedgerRunRow[] {
  return entries.filter(
    (e) => e.event === 'agent_run' && typeof e.subjectId === 'string' && e.subjectId !== '',
  );
}

/** Epoch ms for an ISO string, or NaN when unparseable. */
function epoch(at: string): number {
  const t = Date.parse(at);
  return Number.isFinite(t) ? t : Number.NaN;
}

/**
 * Summarize the ledger's `agent_run` entries per agent. Input order is
 * irrelevant (rows are sorted newest-first internally); rows that aren't
 * agent runs, or lack a subjectId, are ignored. Returns a record keyed by
 * agent id — agents with zero ledgered runs simply have no key (use
 * `agentRunSummary` for a total per-agent lookup).
 */
export function summarizeAgentRuns(
  entries: readonly LedgerRunRow[],
  opts: RunHistoryOptions = {},
): Record<string, AgentRunSummary> {
  const now = opts.now ?? Date.now();
  const recentLimit = Math.max(0, opts.recentLimit ?? DEFAULT_RECENT_LIMIT);
  const activeWindowMs = opts.activeWindowMs ?? ACTIVE_RUN_WINDOW_MS;

  const byAgent = new Map<string, LedgerRunRow[]>();
  for (const row of agentRunRows(entries)) {
    const id = row.subjectId as string;
    const bucket = byAgent.get(id);
    if (bucket) bucket.push(row);
    else byAgent.set(id, [row]);
  }

  const out: Record<string, AgentRunSummary> = {};
  for (const [agentId, rows] of byAgent) {
    const sorted = [...rows].sort((a, b) => (a.at < b.at ? 1 : -1)); // newest-first
    const last = sorted[0];
    const lastMs = epoch(last.at);
    out[agentId] = {
      agentId,
      runCount: sorted.length,
      lastRunAt: last.at,
      recent: sorted.slice(0, recentLimit).map((r) => ({
        at: r.at,
        text: runTextFromSummary(r.summary),
      })),
      // NaN comparisons are false, so an unparseable timestamp is never "active".
      activeNow: now - lastMs <= activeWindowMs && now - lastMs >= 0,
    };
  }
  return out;
}

/**
 * Total per-agent lookup: the agent's summary, or an honest empty one
 * (zero runs, never active) when the ledger has nothing for it.
 */
export function agentRunSummary(
  entries: readonly LedgerRunRow[],
  agentId: string,
  opts: RunHistoryOptions = {},
): AgentRunSummary {
  return (
    summarizeAgentRuns(entries, opts)[agentId] ?? {
      agentId,
      runCount: 0,
      lastRunAt: null,
      recent: [],
      activeNow: false,
    }
  );
}

/**
 * Fleet-level rollup for the Home widget: deployed/running counts from the
 * agents collection, activeNow / runs24h / lastRunAt from the ledger.
 */
export function fleetPulse(
  agents: ReadonlyArray<{ id: string; state: string }>,
  entries: readonly LedgerRunRow[],
  opts: RunHistoryOptions = {},
): FleetPulse {
  const now = opts.now ?? Date.now();
  const summaries = summarizeAgentRuns(entries, opts);
  const runs = agentRunRows(entries);

  let runs24h = 0;
  let lastRunAt: string | null = null;
  for (const row of runs) {
    const t = epoch(row.at);
    if (Number.isFinite(t) && now - t <= FLEET_RUNS_WINDOW_MS && now - t >= 0) runs24h++;
    if (lastRunAt === null || row.at > lastRunAt) lastRunAt = row.at;
  }

  return {
    deployed: agents.length,
    running: agents.filter((a) => a.state === 'running').length,
    activeNow: agents.filter((a) => summaries[a.id]?.activeNow).length,
    runs24h,
    lastRunAt,
  };
}
